interface ValidationTrace<T> {
  schema: Schema<T>;
  value: unknown;
  parent: undefined | ValidationTrace<unknown>;
  key: undefined | string | number;
}

export interface ValidationContext {
  /** When quick is true, don't collect schema violation messages */
  quick: boolean;
  /** Collection of all schema violations from the current validation pass */
  schemaViolations: ValidationTrace<unknown>[];
}

/**
 * A Schema<T> can be used to validate whether a runtime value matches the type T.
 * Typical usage looks like:
 *
 * ```
 * const usableValue = t.assert(schema, unsafeValue);
 * ```
 *
 * `usableValue` will be properly typed, validation will be skipped in
 * production, and the assert call will throw in development if the schema
 * is not satisfied.
 */
export interface Schema<T> {
  validate: (
    obj: unknown,
    validateChild: (
      schema: Schema<unknown>,
      value: unknown,
      key: undefined | string | number
    ) => undefined,
    ctx: ValidationContext
  ) => obj is T;
  getDescription: () => string;
}

export type SchemaType<T extends Schema<any>> = T extends Schema<infer U>
  ? U
  : never;

export function rawExecuteSchema<T>(
  ctx: ValidationContext,
  schema: Schema<T>,
  value: unknown
):
  | { type: 'fail'; schemaViolations: ValidationTrace<unknown>[] }
  | { type: 'pass'; value: T } {
  // extracting the stack from call machinery. This code should be as fast
  // as we can make it, so avoiding heap and frame allocations is
  // beneficial.
  const stack: ValidationTrace<unknown>[] = [
    { schema, value, parent: undefined, key: undefined },
  ];

  let validating: ValidationTrace<unknown>;
  function pushValidation(
    _schema: Schema<unknown>,
    _value: unknown,
    key: undefined | string | number
  ): undefined {
    stack.push({
      schema: _schema,
      value: _value,
      parent: validating,
      key,
    });
    return;
  }
  let violations = 0;

  while (stack.length) {
    validating = stack.pop()!;
    const nextSchema = validating.schema;
    const nextValue = validating.value;

    if (!nextSchema.validate(nextValue, pushValidation, ctx)) {
      if (ctx.quick) {
        return { type: 'fail', schemaViolations: ctx.schemaViolations };
      } else {
        violations += 1;
        ctx.schemaViolations.push(validating);
      }
    }
  }

  if (violations > 0) {
    return { type: 'fail', schemaViolations: ctx.schemaViolations };
  } else {
    return { type: 'pass', value: value as T };
  }
}

export function validate<T>(schema: Schema<T>, value: unknown): value is T {
  return (
    rawExecuteSchema(
      {
        quick: true,
        schemaViolations: [],
      },
      schema,
      value
    ).type === 'pass'
  );
}

export function assert<T>(schema: Schema<T>, value: unknown): T {
  if (process.env.NODE_ENV === 'production') {
    return value as T;
  } else {
    const result = rawExecuteSchema(
      {
        quick: false,
        schemaViolations: [],
      },
      schema,
      value
    );

    if (result.type === 'fail') {
      for (const violation of result.schemaViolations) {
        const keyPath = [];
        let parentViolation: undefined | ValidationTrace<unknown> = violation;
        while (parentViolation !== undefined) {
          if (parentViolation.key !== undefined) {
            keyPath.push(
              typeof parentViolation.key === 'string'
                ? // TODO: strictly speaking string keys may need to be escaped
                  `.${parentViolation.key}`
                : `[${parentViolation.key}]`
            );
          }
          parentViolation = parentViolation.parent;
        }

        console.error(
          `[<root>${keyPath
            .reverse()
            .join(
              ''
            )}] ts-schema expected: ${violation.schema.getDescription()} but received`,
          typeof violation.value === 'string'
            ? `'${violation.value}'`
            : violation.value
        );
      }

      throw new Error(
        `${result.schemaViolations.length} schema violation(s) detected`
      );
    } else {
      return result.value;
    }
  }
}
